import React, { CSSProperties, useMemo, useState } from "react";
import {
  AccountData,
  Follower,
  MembershipPromoCode,
} from "@markit/common.types";
import { Colors } from "../../../utils/colors";
import { CopyableLinkDisplay } from "../../Links/CopyCustomLinkify";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import { getUserData } from "../../../utils/FirebaseUtils";
import {
  getMembersFromGroupPromoCode,
  getMembershipPromoCodeUsageCount,
} from "../../../utils/membershipUtils";
import { MARKITAI } from "@markit/common.utils";

type GroupPlanOwnerShareProps = {
  creatorData: AccountData;
  membershipPromoCode: MembershipPromoCode;
  followerData: Follower;
  containerStyles?: CSSProperties;
};

const GroupPlanOwnerShare = (props: GroupPlanOwnerShareProps) => {
  const { creatorData, membershipPromoCode, followerData, containerStyles } =
    props;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [usageCount, setUsageCount] = useState<number>(0);
  const [groupMembersData, setGroupMembersData] = useState<AccountData[]>([]);

  const membershipPlan = useMemo(
    () =>
      creatorData.membership.membershipPlans.find(
        (plan) => plan.id === followerData.membershipPlanId
      ),
    [creatorData.membership.membershipPlans, followerData.membershipPlanId]
  );

  useAsyncOnMount(async () => {
    try {
      const usedCount = await getMembershipPromoCodeUsageCount(
        creatorData.uid,
        membershipPromoCode.id
      );
      setUsageCount(usedCount);

      const members = await getMembersFromGroupPromoCode(
        creatorData.uid,
        membershipPromoCode.id
      );
      const membersUserData = await Promise.all(
        members.map((follower) => getUserData(follower.uid))
      );

      setGroupMembersData(
        membersUserData.filter((member) => member !== undefined)
      );
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching MembershipPromoCode:", error);
    }
  });

  if (isLoading) {
    return null;
  }

  return (
    <div className="ColumnNormal" style={{ gap: 14, ...containerStyles }}>
      <div className="ColumnNormal" style={{ gap: 4 }}>
        <span className="bodyMedium">
          {usageCount}/{membershipPromoCode?.maxRedemptions} Group Invites Used
        </span>
        {groupMembersData.length ? (
          <span className="bodySmallRegular">
            Current group members:{" "}
            {groupMembersData.map((member, index) => (
              <span key={member.uid} className="bodySmallRegular">
                {member.fullName}
                {index !== groupMembersData.length - 1 && ", "}
              </span>
            ))}
          </span>
        ) : null}
        <span className="smallBodySubtext">
          Copy and share the code to invite up to{" "}
          {membershipPromoCode?.maxRedemptions} people to join your group plan.
          To join, they must apply your code at checkout for the &apos;
          {membershipPlan?.name ?? ""}&apos; plan.
        </span>
      </div>
      <CopyableLinkDisplay
        copyText={`Enter my code ${
          membershipPromoCode?.alias ?? ""
        } at checkout for the '${
          membershipPlan?.name ?? ""
        }' plan. ${MARKITAI}/u/${creatorData.username}/subscribe`}
        multiLineText={true}
        containerStyles={{
          padding: 14,
          backgroundColor: Colors.GRAY6,
          borderColor: Colors.GRAY1,
          borderWidth: 0.5,
        }}
      />
    </div>
  );
};

export default GroupPlanOwnerShare;
